import React, { useState, useEffect } from "react";
import { useQuery } from "@apollo/client";
import { GET_STAGE_COUNTS, GET_STATUS_COUNTS } from "../services/userApi";
import MetricCard from "./MetricCard";

const LEAD_CATEGORY_NAMES = {
  4: "Aadhar Mitra",
  3: "Customer",
};

const STATUS_CATEGORY_NAMES = {
  4: "Aadhar Mitra",
  3: "Customer",
};

// Helper function to sort and add category names to stages
const sortStagesByCategory = (stages) => {
  if (!stages) return [];
  const sortedStages = [...stages].sort(
    (a, b) => a.leadCategoryId - b.leadCategoryId
  );
  return sortedStages.map((stage) => ({
    ...stage,
    categoryName: LEAD_CATEGORY_NAMES[stage.leadCategoryId] || null,
  }));
};

// Helper function to sort and add category names to status
const sortStatusByCategory = (statuses) => {
  if (!statuses) return [];
  const sortedStatuses = [...statuses].sort((a, b) => {
    const aCategoryId = a.leadCategoryId || 0;
    const bCategoryId = b.leadCategoryId || 0;
    return aCategoryId - bCategoryId;
  });
  return sortedStatuses.map((status) => ({
    ...status,
    categoryName: status.leadCategoryId
      ? STATUS_CATEGORY_NAMES[status.leadCategoryId] || null
      : null,
  }));
};

const SkeletonMetricCard = () => (
  <div className="animate-pulse bg-white p-4 rounded-lg border">
    <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
    <div className="h-8 bg-gray-200 rounded w-1/2"></div>
  </div>
);

const LeadMetrics = ({
  userId,
  jobRoleId,
  onStageClick,
  onStatusClick,
  showStages = true,
  showStatus = true,
}) => {
  const [metricCardsData, setMetricCardsData] = useState([]);
  const [statusCardsData, setStatusCardsData] = useState([]);

  const {
    data: stageCountsData,
    loading: metricCardsLoading,
    error: stageCountsError,
    refetch: refetchStages,
  } = useQuery(GET_STAGE_COUNTS, {
    variables: { jobroleId: jobRoleId, userId: userId },
    skip: !jobRoleId  ,
    fetchPolicy: "cache-and-network",
  });

  const {
    data: statusCountsData,
    loading: statusCardsLoading,
    refetch: refetchStatus,
  } = useQuery(GET_STATUS_COUNTS, {
    variables: { jobroleId: jobRoleId, userId: userId },
    skip: !jobRoleId  ,
    fetchPolicy: "cache-and-network",
  });

  useEffect(() => {
    if (jobRoleId && userId && showStages) {
      refetchStages();
    }
  }, [jobRoleId, userId, showStages, refetchStages]);

  useEffect(() => {
    if (jobRoleId && userId && showStatus) {
      refetchStatus();
    }
  }, [jobRoleId, userId, showStatus, refetchStatus]);

  useEffect(() => {
    if (stageCountsData) {
      const sortedStages = sortStagesByCategory(
        stageCountsData.getCountStageForJobrole
      );
      setMetricCardsData(sortedStages);
    }
  }, [stageCountsData]);

  useEffect(() => {
    if (statusCountsData) {
      const sortedStatuses = sortStatusByCategory(
        statusCountsData.getCountStatusForJobrole
      );
      setStatusCardsData(sortedStatuses);
    }
  }, [statusCountsData]);

  return (
    <>
      {showStages && (
        <>
          <h3>Leads Count In Stages:</h3>
          <div className="metrics-grid">
            {metricCardsLoading
              ? Array.from({ length: 6 }).map((_, index) => (
                  <SkeletonMetricCard key={`stage-skeleton-${index}`} />
                ))
              : metricCardsData &&
                metricCardsData.map((card) => (
                  <MetricCard
                    key={card.id}
                    title={card.stageName}
                    value={card.count}
                    color={
                      card.colorCode === null
                        ? "#000000"
                        : "#" +
                          card.colorCode.substring(2, card.colorCode.length)
                    }
                    categoryName={card.categoryName}
                    onClick={() => onStageClick && onStageClick(card.id)}
                  />
                ))}
          </div>
        </>
      )}

      {showStatus && (
        <>
          <h3>Leads Count In Status:</h3>
          <div className="metrics-grid">
            {statusCardsLoading
              ? Array.from({ length: 4 }).map((_, index) => (
                  <SkeletonMetricCard key={`status-skeleton-${index}`} />
                ))
              : statusCardsData &&
                statusCardsData.map((card) => (
                  <MetricCard
                    key={card.id}
                    title={card.statusName}
                    value={card.count}
                    color={
                      card.colorCode === null
                        ? "#000000"
                        : "#" +
                          card.colorCode.substring(2, card.colorCode.length)
                    }
                    categoryName={card.categoryName}
                    onClick={() => onStatusClick && onStatusClick(card.id)}
                  />
                ))}
          </div>
        </>
      )}
    </>
  );
};

export default LeadMetrics;
